/**
 * Shared session-expired state, consumed by `SessionExpiredModal.vue` and
 * tripped by `authErrorHandler`/API call sites on auth failures.
 *
 * Exposes a single reactive on/off flag plus the message to display, and a
 * `triggerSessionExpired` setter that acts as a circuit breaker so a burst of
 * concurrent 401/403 responses only shows one popup instead of one per call.
 */

import { ref } from 'vue';

// Session-expired circuit breaker: a dead session means every in-flight or
// upcoming API call fails with 401/403. Without this, each failing request
// would trigger its own toast/redirect/logout. `triggerSessionExpired` trips
// once — the first 401/403 shows the popup, every other call (parallel or
// later) is a no-op until a full page reload (post-login) resets it.
/** Reactive flag: true once a session-expired condition has been triggered. */
export const sessionExpired = ref(false);
/** Reactive message to show alongside the session-expired state. */
export const sessionExpiredMessage = ref('');

const DEFAULT_MESSAGE = 'Your session has expired. Please log in again to continue.';

/**
 * Trips the session-expired circuit breaker, showing the session-expired UI.
 *
 * No-ops if the breaker is already tripped (`sessionExpired.value` is true),
 * so only the first caller in a burst of failures has any effect. Also no-ops
 * if there is no `user` entry in localStorage, since a guest who was never
 * logged in getting a 401 is "not authenticated", not "session expired".
 *
 * @param {string} [message] Custom message to display; falls back to a default session-expired message.
 */
export function triggerSessionExpired(message) {
  if (sessionExpired.value) return;

  // A guest who was never logged in gets 401s from plenty of endpoints —
  // that's just "not authenticated", not "your session expired". Only show
  // the popup when there was actually a logged-in session to lose.
  let hadSession = false;
  try {
    hadSession = !!localStorage.getItem('user');
  } catch {
    hadSession = false;
  }
  if (!hadSession) return;

  sessionExpiredMessage.value = message || DEFAULT_MESSAGE;
  sessionExpired.value = true;
}
